import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { isNativeApp } from '../config';

/**
 * Hook that checks if the app version is up to date.
 * On native (Capacitor), it reads the version from the App plugin.
 * On web, version checking is skipped.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - whether the check is active
 * @returns {{ updateAvailable, forceUpdate, latestVersion, updateMessage, storeUrl, dismissed, dismiss }}
 */
export function useVersionCheck({ enabled = true } = {}) {
  const [state, setState] = useState({
    updateAvailable: false,
    forceUpdate: false,
    latestVersion: '',
    updateMessage: '',
    storeUrl: '',
  });
  const [dismissed, setDismissed] = useState(false);
  const checkedRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (!isNativeApp()) return;

    try {
      const { App } = await import('@capacitor/app');
      const info = await App.getInfo();
      const currentVersion = info.version; // e.g. "1.0.0"

      // Detect platform
      const platform = window.Capacitor?.getPlatform?.() || 'ios';
      if (platform !== 'ios' && platform !== 'android') return;

      const data = await api.checkAppVersion(platform, currentVersion);

      if (data.updateAvailable || data.forceUpdate) {
        setState({
          updateAvailable: data.updateAvailable,
          forceUpdate: data.forceUpdate,
          latestVersion: data.latestVersion || '',
          updateMessage: data.updateMessage || '',
          storeUrl: data.storeUrl || '',
        });
        // If forced, never allow dismiss
        if (data.forceUpdate) {
          setDismissed(false);
        }
      }
    } catch (err) {
      // Fail silently — don't block the user
      console.error('Version check failed:', err);
    }
  }, []);

  useEffect(() => {
    if (!enabled || checkedRef.current) return;
    checkedRef.current = true;
    checkVersion();
  }, [enabled, checkVersion]);

  const dismiss = useCallback(() => {
    if (!state.forceUpdate) {
      setDismissed(true);
    }
  }, [state.forceUpdate]);

  return {
    ...state,
    dismissed,
    dismiss,
  };
}
